import { type MutationOptions, useMutation } from "@tanstack/react-query";
import { useApi } from "./use-api.ts";

// Special property to pass dynamic URL
export type WithDynamicUrl<T> = T extends void ? { _url?: string } : T & { _url?: string };

export type ApiMutationOptions<T, R> = Omit<
  MutationOptions<R, Error, WithDynamicUrl<T>>,
  "mutationKey" | "mutationFn"
>;

export const useApiMutation = <T = Record<string, unknown>, R = unknown>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  query: string,
  opts?: ApiMutationOptions<T, R>,
  tokenOverride?: string,
) => {
  const { callApi } = useApi<R>(tokenOverride);

  return useMutation({
    mutationKey: [query],
    mutationFn: async (data: WithDynamicUrl<T> = {} as WithDynamicUrl<T>) => {
      const dataObj = data || {};
      // Safely extract _url without assuming data is an object
      const _url =
        typeof dataObj === "object" && dataObj !== null
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (dataObj as any)._url
          : undefined;
      const rest =
        typeof dataObj === "object" && dataObj !== null
          ? Object.fromEntries(Object.entries(dataObj).filter(([key]) => key !== "_url"))
          : dataObj;

      const finalUrl = _url || query;
      // If data is undefined or empty object, don't send a body

      const hasData = typeof dataObj === "object" && Object.keys(rest).length > 0;

      return callApi(finalUrl, {
        method,
        headers: hasData
          ? {
              "Content-Type": "application/json",
            }
          : undefined,
        body: hasData ? JSON.stringify(rest) : undefined,
      });
    },
    ...opts,
  });
};
